
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { JournalStage } from "@/hooks/useJournalFlow";
import StuckPointField from "./form/StuckPointField";
import PromptResponseField from "./form/PromptResponseField";
import JournalFormActions from "./form/JournalFormActions";
import DifficultySelector from "./form/DifficultySelector";
import { journalSchema, JournalFormValues } from "./form/JournalFormSchema";
import { DifficultyRank } from "@/utils/difficultyRanks";

interface JournalFormProps {
  defaultValues?: JournalFormValues;
  currentStage: JournalStage;
  diceValue: number;
  twistActive: boolean;
  onContinue: () => void;
  onRollDice: () => void;
  onSave: (values: JournalFormValues, difficultyRank?: DifficultyRank) => void;
  journalPrompts: Record<number, {
    perception: string;
    intention: string;
    nextStep: string;
  }>;
  hasDiceRolled?: boolean;
  userHasStartedTyping?: boolean;
  onUserStartedTyping?: () => void;
  onReRoll: () => void;
  existingDifficultyRank?: DifficultyRank | null;
  isNewEntry?: boolean;
}

const JournalForm: React.FC<JournalFormProps> = ({
  defaultValues,
  currentStage,
  diceValue,
  twistActive,
  onContinue,
  onRollDice,
  onSave,
  journalPrompts,
  hasDiceRolled = false,
  userHasStartedTyping = false,
  onUserStartedTyping,
  onReRoll,
  existingDifficultyRank,
  isNewEntry = false
}) => {
  const currentPrompts = diceValue > 0 && diceValue <= 6 ? journalPrompts[diceValue] : null;
  const lastResetValues = useRef<string>("");
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyRank>(
    existingDifficultyRank || 'moderate'
  );
  
  console.log("JournalForm render:", {
    currentStage,
    diceValue,
    hasDiceRolled,
    currentPrompts: currentPrompts ? true : false,
    hasValidPrompts: !!currentPrompts,
    defaultValues,
    userHasStartedTyping
  });

  const form = useForm<JournalFormValues>({
    resolver: zodResolver(journalSchema),
    defaultValues: defaultValues || {
      stuck_point: "",
      perception_response: "",
      intention_response: "",
      next_step_response: "",
      twist_response: "",
    },
    mode: "onChange",
  });

  // FIXED: Only reset form when absolutely necessary and protect user input
  useEffect(() => {
    if (!defaultValues) {
      console.log("JournalForm: No defaultValues provided, skipping reset");
      return;
    }

    const defaultValuesString = JSON.stringify(defaultValues);
    const currentFormValues = form.getValues();
    const currentFormValuesString = JSON.stringify(currentFormValues);
    
    console.log("JournalForm: Checking if form reset needed");
    console.log("JournalForm: defaultValues:", defaultValues);
    console.log("JournalForm: currentFormValues:", currentFormValues);
    console.log("JournalForm: userHasStartedTyping:", userHasStartedTyping);
    console.log("JournalForm: lastResetValues:", lastResetValues.current);
    console.log("JournalForm: defaultValuesString:", defaultValuesString);

    // FIX: Don't reset if user has started typing
    if (userHasStartedTyping) {
      console.log("JournalForm: SKIPPING reset - user has started typing");
      return;
    }

    // FIX: Don't reset if values haven't actually changed
    if (defaultValuesString === currentFormValuesString) {
      console.log("JournalForm: SKIPPING reset - values are identical");
      return;
    }

    // FIX: Don't reset if we've already reset with these exact values
    if (lastResetValues.current === defaultValuesString) {
      console.log("JournalForm: SKIPPING reset - already reset with these values");
      return;
    }

    console.log("JournalForm: CALLING form.reset() with values:", defaultValues);
    lastResetValues.current = defaultValuesString;
    form.reset(defaultValues);
  }, [defaultValues, form, userHasStartedTyping]);

  const handleSubmit = (values: JournalFormValues) => {
    console.log("JournalForm: Form submitted with values:", values);
    onSave(values, selectedDifficulty);
  };

  // Handle user starting to type
  const handleFormChange = () => {
    if (!userHasStartedTyping && onUserStartedTyping) {
      console.log("JournalForm: User started typing - calling onUserStartedTyping");
      onUserStartedTyping();
    }
  };

  // Enhanced field visibility logic with detailed logs
  const shouldShowPerceptionField = () => {
    const hasValidDiceValue = diceValue > 0 && diceValue <= 6;
    const hasValidPrompts = !!currentPrompts;
    const isCorrectStage = currentStage === "perception" || 
                         currentStage === "intention" || 
                         currentStage === "nextStep" || 
                         currentStage === "completed";
    
    const show = hasDiceRolled && hasValidDiceValue && hasValidPrompts && isCorrectStage;
    
    console.log("JournalForm: shouldShowPerceptionField:", show, {
      hasDiceRolled,
      diceValue,
      hasValidDiceValue,
      hasValidPrompts,
      currentStage,
      isCorrectStage,
      allConditionsMet: show
    });
    
    return show;
  };

  const shouldShowIntentionField = () => {
    const hasValidDiceValue = diceValue > 0 && diceValue <= 6;
    const hasValidPrompts = !!currentPrompts;
    const isCorrectStage = currentStage === "intention" || 
                         currentStage === "nextStep" || 
                         currentStage === "completed";
    
    const show = hasDiceRolled && hasValidDiceValue && hasValidPrompts && isCorrectStage;
    
    console.log("JournalForm: shouldShowIntentionField:", show, {
      hasDiceRolled,
      diceValue,
      hasValidDiceValue,
      hasValidPrompts,
      currentStage,
      isCorrectStage,
      allConditionsMet: show
    });
    
    return show;
  };

  const shouldShowNextStepField = () => {
    const hasValidDiceValue = diceValue > 0 && diceValue <= 6;
    const hasValidPrompts = !!currentPrompts;
    const isCorrectStage = currentStage === "nextStep" || currentStage === "completed";
    
    const show = hasDiceRolled && hasValidDiceValue && hasValidPrompts && isCorrectStage;
    
    console.log("JournalForm: shouldShowNextStepField:", show, {
      hasDiceRolled,
      diceValue,
      hasValidDiceValue,
      hasValidPrompts,
      currentStage,
      isCorrectStage,
      allConditionsMet: show
    });
    
    return show;
  };

  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(handleSubmit)} 
        className="space-y-6"
        onChange={handleFormChange}
      >
        {/* Always show stuck point field */}
        <StuckPointField 
          form={form} 
          onUserStartedTyping={onUserStartedTyping}
        />
        
        {/* Show difficulty selector for new entries */}
        {isNewEntry && currentStage === "stuck" && (
          <DifficultySelector
            value={selectedDifficulty}
            onChange={setSelectedDifficulty}
            disabled={hasDiceRolled}
          />
        )}
        
        {/* Show perception field after dice roll */}
        {shouldShowPerceptionField() && (
          <PromptResponseField 
            form={form} 
            name="perception_response" 
            label={currentPrompts!.perception}
            onUserStartedTyping={onUserStartedTyping}
          />
        )}
        
        {/* Show intention field after advancing to intention stage */}
        {shouldShowIntentionField() && (
          <PromptResponseField 
            form={form} 
            name="intention_response" 
            label={currentPrompts!.intention}
            onUserStartedTyping={onUserStartedTyping}
          />
        )}
        
        {/* Show next step field after advancing to nextStep stage */}
        {shouldShowNextStepField() && (
          <PromptResponseField 
            form={form} 
            name="next_step_response" 
            label={currentPrompts!.nextStep}
            onUserStartedTyping={onUserStartedTyping}
          />
        )}
        
        {/* Show twist field if twist is active */}
        {currentStage === "twist" && twistActive && hasDiceRolled && (
          <PromptResponseField 
            form={form} 
            name="twist_response" 
            label="What's the twist?"
            onUserStartedTyping={onUserStartedTyping}
          />
        )}
        
        <JournalFormActions
          currentStage={currentStage}
          onContinue={onContinue}
          onRollDice={onRollDice}
          hasDiceRolled={hasDiceRolled}
          onReRoll={onReRoll}
        />
      </form>
    </Form>
  );
};

export default JournalForm;
